import { Reveal, Eyebrow } from "../components/anim";
import Starfield from "../components/Starfield";

const PAGES = {
  terms: {
    title: "Terms of Service",
    updated: "Last updated: July 1, 2026",
    sections: [
      {
        h: "1. Services",
        p: "G-Studio provides custom website design and development. Project scope and pricing are discussed on your consultation call and always confirmed with you in writing before work begins.",
      },
      {
        h: "2. Billing",
        p: "Project fees are invoiced via Stripe after your consultation, once scope and price are approved by you in writing. We do not bill hourly and we do not add fees you have not approved.",
      },
      {
        h: "3. Client Responsibilities",
        p: "You agree to provide accurate business information, brand assets and content needed to complete your build, and to review deliverables within a reasonable timeframe. Delays in providing materials may extend your launch date.",
      },
      {
        h: "4. Ownership",
        p: "G-Studio retains ownership of the website's design and code and grants you a license to use them while your hosting is active. Your business content — text, photos and logo — always remains yours. G-Studio may display the work in its portfolio unless you request otherwise in writing.",
      },
      {
        h: "5. Acceptable Use",
        p: "Websites we build may not be used for unlawful, deceptive or harmful content. We reserve the right to decline or end a project for violations, with refunds handled under our Refund Policy.",
      },
      {
        h: "6. Limitation of Liability",
        p: "Our total liability for any claim arising from our services is limited to the amounts you have paid us in the 12 months preceding the claim. We are not liable for indirect or consequential losses, including lost revenue.",
      },
      {
        h: "7. Contact",
        p: "Questions about these terms can be sent to bengall.work3@gmail.com.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: July 1, 2026",
    sections: [
      {
        h: "1. What We Collect",
        p: "We collect the information you share with us directly: your name, email address, phone number and business details from calls and messages, plus billing details processed by Stripe when we invoice you.",
      },
      {
        h: "2. Payments",
        p: "All payments are processed by Stripe, Inc. G-Studio never sees, stores or has access to your full card number. Stripe's own privacy policy governs the payment data it processes.",
      },
      {
        h: "3. How We Use Information",
        p: "We use your information to deliver our services: scoping and building your website, sending receipts and project updates, and responding to enquiries. We do not sell your personal information to anyone.",
      },
      {
        h: "4. Data Retention",
        p: "Project and billing records are kept while your account is active and for a reasonable period afterwards to meet legal and accounting obligations. You may request deletion of your personal data at any time.",
      },
      {
        h: "5. Your Rights",
        p: "You can request a copy, correction or deletion of your personal information by emailing bengall.work3@gmail.com. We respond to all requests within 30 days.",
      },
    ],
  },
  refund: {
    title: "Refund Policy",
    updated: "Last updated: July 1, 2026",
    sections: [
      {
        h: "1. Before Work Begins",
        p: "If you cancel your project before design work has started, your payment is refunded in full — no questions, no fees.",
      },
      {
        h: "2. After Work Has Started",
        p: "Once design work has started but before your first design review is delivered, 50% of your payment is refundable. After the first design review has been delivered, the payment is non-refundable because the work has been performed.",
      },
      {
        h: "3. Additional Work",
        p: "Any separately quoted additions follow the same rule — fully refundable until work on them has begun, and always quoted upfront so you approve the cost first.",
      },
      {
        h: "4. If We Miss the Mark",
        p: "If we materially fail to deliver what was agreed in your project scope, we will fix it at no cost to you — or refund the affected portion of your payment at your request.",
      },
      {
        h: "5. How to Request",
        p: "Email bengall.work3@gmail.com with your order details. Approved refunds are issued to your original payment method via Stripe within 5–10 business days.",
      },
    ],
  },
};

const LegalPage = ({ pageKey, testId }) => {
  const page = PAGES[pageKey];
  return (
    <main className="relative min-h-screen overflow-hidden pt-28 pb-20" data-testid={testId}>
        <Starfield />
        <div className="pointer-events-none absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full bg-violet-600/15 blur-[130px]" />
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">{page.title}</h1>
          <p className="mt-3 font-mono2 text-xs uppercase tracking-[0.18em] text-slate-400">{page.updated}</p>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="mt-10 space-y-8 rounded-3xl border border-white/10 bg-white/5 p-8 sm:p-10">
            {page.sections.map((s) => (
              <section key={s.h}>
                <h2 className="font-display text-lg font-semibold text-white">{s.h}</h2>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{s.p}</p>
              </section>
            ))}
          </div>
        </Reveal>
      </div>
    </main>
  );
};

export const Terms = () => <LegalPage pageKey="terms" testId="terms-page" />;
export const Privacy = () => <LegalPage pageKey="privacy" testId="privacy-page" />;
export const RefundPolicy = () => <LegalPage pageKey="refund" testId="refund-page" />;
